import { API_ERROR, type ApiErrorCode, HTTP_STATUS } from '../../shared/constants.ts';
import { isOnlineOnlyRoute } from '../../shared/offline-policy.ts';
import { reportSessionEnded } from '../auth/expiry.ts';
import {
  entityKeyFor,
  type OfflineFanOut,
  type OfflineWrite,
  queueWrite,
} from '../offline/queue.ts';
import { setOfflineState } from '../offline/state.ts';
import { getEntity, putEntity } from '../offline/store.ts';

// Thin fetch wrapper for the JSON API. Every response body is validated by a
// parser (a zod/mini schema, or NO_CONTENT for empty replies) before it
// reaches a component, so the client never trusts the wire shape blindly,
// and every non-2xx becomes an ApiError with the server's machine-readable
// code. A 401 `unauthenticated` from any route but the auth ones means the
// session ended mid-visit; that is reported so the app can sign the person
// out properly instead of leaving every page failing.
//
// It is also where the offline copy is read and written. A read that
// succeeds is kept, so the same page opens again with no signal; a read that
// cannot reach the server is answered from what was kept, and only fails
// when nothing was. A write that cannot reach the server goes into the queue
// — but only when its caller said what the write means offline
// (`options.offline`). That is the boundary in one place: a room, an item, a
// note, a photo have an offline meaning; an account, a token, a webhook, a
// share link, a report, an export do not, so they still fail with "Could not
// reach the server", which is the truth.

/** Routes where a 401 is an answer (bad credentials), not an ended session. */
const AUTH_ROUTE_PREFIX = '/api/auth/';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

export interface ApiRequestOptions {
  readonly method?: HttpMethod;
  readonly body?: unknown;
  /** A multipart body; the browser sets the boundary header itself. */
  readonly form?: FormData;
  /** TanStack Query's signal, so a superseded read is really cancelled. */
  readonly signal?: AbortSignal | undefined;
  /**
   * What this write means to the offline copy. Present: it is queued when
   * there is no connection and answered with `reply`. Absent: it needs a
   * connection and fails without one. A request that stands for several
   * writes — the runs a pattern makes — passes an `OfflineFanOut` instead,
   * which queues them itself and answers the same reply the route would.
   */
  readonly offline?: OfflineWrite | OfflineFanOut;
}

/** Anything with zod's `parse` contract; keeps this module free of zod. */
export interface Parser<T> {
  parse(data: unknown): T;
}

/** Parser for 204 replies: the body must be absent. */
export const NO_CONTENT: Parser<undefined> = {
  parse(data: unknown): undefined {
    if (data !== undefined) {
      throw new TypeError('Expected an empty response body');
    }
    return data;
  },
};

export interface ApiIssue {
  readonly path: string;
  readonly message: string;
}

export class ApiError extends Error {
  override readonly name = 'ApiError';

  constructor(
    readonly status: number,
    readonly code: ApiErrorCode,
    message: string,
    readonly issues: readonly ApiIssue[] = [],
    /** The server's id for the request, from the error body; null when the body had none. */
    readonly requestId: string | null = null,
  ) {
    super(message);
  }
}

interface WireError {
  readonly error?: unknown;
  readonly message?: unknown;
  readonly issues?: unknown;
  readonly requestId?: unknown;
}

function isErrorCode(value: unknown): value is ApiErrorCode {
  return typeof value === 'string' && Object.values<string>(API_ERROR).includes(value);
}

function toIssues(value: unknown): ApiIssue[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.flatMap((entry: unknown) => {
    if (typeof entry !== 'object' || entry === null) {
      return [];
    }
    const { path, message } = entry as { path?: unknown; message?: unknown };
    return typeof path === 'string' && typeof message === 'string' ? [{ path, message }] : [];
  });
}

async function errorFrom(response: Response): Promise<ApiError> {
  let wire: WireError = {};
  try {
    wire = (await response.json()) as WireError;
  } catch {
    // A non-JSON error body (a proxy page, for instance) still becomes an
    // ApiError; the status code is the only reliable information then.
  }
  const code = isErrorCode(wire.error) ? wire.error : API_ERROR.INTERNAL;
  const message =
    typeof wire.message === 'string'
      ? wire.message
      : `Request failed with status ${String(response.status)}`;
  const requestId =
    typeof wire.requestId === 'string' && wire.requestId !== '' ? wire.requestId : null;
  return new ApiError(response.status, code, message, toIssues(wire.issues), requestId);
}

/**
 * The request id worth quoting: a 5xx is a failure nobody wrote a message
 * for, and its id finds the Worker's log line. A 4xx already says what was
 * wrong in words, so its id would only be noise beside them.
 */
function failureReference(error: ApiError): string | null {
  return error.status >= HTTP_STATUS.INTERNAL_SERVER_ERROR ? error.requestId : null;
}

/**
 * What to tell the person when a request failed: the API's words (with the
 * reference to quote when the failure was the server's own), or a network
 * hint.
 */
export function describeApiFailure(error: unknown): string {
  if (error instanceof ApiError) {
    const reference = failureReference(error);
    return reference === null ? error.message : `${error.message} (Reference ${reference})`;
  }
  return 'Could not reach the server. Check your connection and try again.';
}

/** Perform a request and validate the response with `parser`. */
export async function apiRequest<T>(
  path: string,
  parser: Parser<T>,
  options: ApiRequestOptions = {},
): Promise<T> {
  const method = options.method ?? 'GET';
  const headers = new Headers({ Accept: 'application/json' });
  const init: RequestInit = { method, credentials: 'same-origin', headers };
  if (options.body !== undefined) {
    headers.set('Content-Type', 'application/json');
    init.body = JSON.stringify(options.body);
  } else if (options.form !== undefined) {
    init.body = options.form;
  }
  if (options.signal !== undefined) {
    init.signal = options.signal;
  }
  // A browser that already knows it has no connection is taken at its word:
  // trying anyway costs the person a timeout before the same answer.
  if (offline()) {
    return await withoutNetwork(path, parser, options);
  }
  let response: Response;
  try {
    response = await fetch(path, init);
  } catch (error) {
    // A request the browser could not send at all: a lost signal, a dropped
    // connection, an aborted read. An abort is the app's own doing (a
    // superseded query) and must not be answered from the copy.
    if (isAbort(error)) {
      throw error;
    }
    return await withoutNetwork(path, parser, options, error);
  }
  if (!response.ok) {
    const error = await errorFrom(response);
    if (sessionEndedBy(path, error)) {
      reportSessionEnded();
    }
    throw error;
  }
  const body = await readBody(response);
  const parsed = parser.parse(body);
  if (method === 'GET') {
    setOfflineState({ servingOffline: false });
    // Not awaited: the page has its answer, and keeping a copy of it is this
    // device's own business. Making the read wait on IndexedDB would slow
    // every page down for a store nothing on screen is reading.
    if (!isOnlineOnlyRoute(path)) {
      void keepRead(path, body, response).catch((error: unknown) => {
        console.warn('The read could not be kept for offline use', error);
      });
    }
  }
  return parsed;
}

/** Whether the browser already knows it has no connection. */
function offline(): boolean {
  return typeof navigator !== 'undefined' && !navigator.onLine;
}

/** An aborted request, which the app itself asked for. */
function isAbort(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

/** Keep a successful read, with the row version its next write will quote. */
async function keepRead(path: string, body: unknown, response: Response): Promise<void> {
  const tag = response.headers.get('ETag');
  await putEntity({
    key: entityKeyFor(path),
    body,
    updatedAt: tag === null ? null : tag.replaceAll('"', ''),
    keptAt: new Date().toISOString(),
  });
}

/**
 * What to do when the server cannot be reached: answer a read from the kept
 * copy, or put a write in the queue and answer with what it will become.
 * Anything else throws what it would have thrown before any of this existed.
 */
/**
 * What to throw when there is nothing kept and nothing to queue: the failure
 * the browser gave, or the one `fetch` would have given had the browser
 * bothered to try (`navigator.onLine` said not to). `describeApiFailure`
 * turns either into "Could not reach the server".
 */
function asError(cause: unknown): Error {
  return cause instanceof Error ? cause : new TypeError('Failed to fetch');
}

async function withoutNetwork<T>(
  path: string,
  parser: Parser<T>,
  options: ApiRequestOptions,
  cause?: unknown,
): Promise<T> {
  if (isOnlineOnlyRoute(path)) {
    throw asError(cause);
  }
  const method = options.method ?? 'GET';
  if (method === 'GET') {
    const kept = await getEntity(entityKeyFor(path));
    if (kept === null) {
      throw asError(cause);
    }
    setOfflineState({ servingOffline: true });
    return parser.parse(kept.body);
  }
  const { offline: write } = options;
  if (write === undefined) {
    throw asError(cause);
  }
  // A fan-out queues the several writes the one request stood for and
  // answers for all of them; anything else is the one write it describes.
  return parser.parse(
    typeof write === 'function'
      ? await write()
      : await queueWrite({ method, path, body: options.body, form: options.form }, write),
  );
}

function sessionEndedBy(path: string, error: ApiError): boolean {
  return (
    error.status === HTTP_STATUS.UNAUTHORIZED &&
    error.code === API_ERROR.UNAUTHENTICATED &&
    !path.startsWith(AUTH_ROUTE_PREFIX)
  );
}

/** JSON body, or nothing for 204 so NO_CONTENT validates it. */
async function readBody(response: Response): Promise<unknown> {
  if (response.status === HTTP_STATUS.NO_CONTENT) {
    return;
  }
  return await response.json();
}
